import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import PayslipPDF from '../components/PayslipPDF';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FileText, Download, Wallet, Eye, EyeOff, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MASKED = '₹••,•••.••';

const MyPayslips = ({ user, onLogout }) => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [privacyOn, setPrivacyOn] = useState(user?.role !== 'admin');

  const fetchPayslips = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/payroll/my-payslips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayslips(res.data.payslips || []);
    } catch (e) {
      toast.error('Failed to fetch payslips');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPayslips(); }, [fetchPayslips]);

  const viewPayslip = async (payslipId) => {
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/payroll/payslip/${payslipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPayslip(res.data);
    } catch (e) {
      toast.error('Failed to load payslip');
    } finally {
      setDetailLoading(false);
    }
  };

  const amt = (n) => privacyOn ? MASKED : formatCurrency(n);

  // Group payslips by year
  const groupedByYear = payslips.reduce((acc, p) => {
    const y = p.year;
    if (!acc[y]) acc[y] = [];
    acc[y].push(p);
    return acc;
  }, {});
  const years = Object.keys(groupedByYear).sort((a, b) => b - a);

  if (loading) return (
    <div className="flex min-h-screen bg-slate-900">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="flex-1 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-slate-900" data-testid="my-payslips-page">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-6 overflow-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Wallet className="w-8 h-8 text-emerald-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">My Payslips</h1>
                <p className="text-slate-400 text-sm">View and download your salary slips</p>
              </div>
            </div>

            {/* Privacy Toggle */}
            <button
              onClick={() => setPrivacyOn(prev => !prev)}
              data-testid="privacy-toggle"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                privacyOn
                  ? 'bg-slate-700 text-amber-400 border border-amber-500/30 hover:bg-slate-600'
                  : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20'
              }`}
            >
              {privacyOn ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {privacyOn ? (
                <><ShieldCheck className="w-3.5 h-3.5" /> Privacy On — Tap to reveal</>
              ) : (
                'Amounts visible'
              )}
            </button>
          </div>

          {/* Summary Cards */}
          {payslips.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <p className="text-slate-400 text-xs mb-1">Latest Net Pay</p>
                  <p className={`text-2xl font-bold ${privacyOn ? 'text-slate-600 select-none' : 'text-emerald-400'}`} data-testid="latest-net-pay">
                    {amt(payslips[0]?.net_pay)}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">{payslips[0]?.month_name} {payslips[0]?.year}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <p className="text-slate-400 text-xs mb-1">YTD Earnings</p>
                  <p className={`text-2xl font-bold ${privacyOn ? 'text-slate-600 select-none' : 'text-white'}`}>
                    {privacyOn ? MASKED : formatCurrency(
                      payslips.filter(p => p.year === new Date().getFullYear())
                        .reduce((s, p) => s + (p.gross_earnings || 0), 0)
                    )}
                  </p>
                  <p className="text-slate-500 text-xs mt-1">{new Date().getFullYear()} Total</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <p className="text-slate-400 text-xs mb-1">Total Payslips</p>
                  <p className="text-2xl font-bold text-white">{payslips.length}</p>
                  <p className="text-slate-500 text-xs mt-1">All time</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Payslips by Year */}
          {payslips.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="py-16 text-center">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">No payslips available yet</p>
                <p className="text-slate-500 text-sm mt-1">Your salary slips will appear here once processed</p>
              </CardContent>
            </Card>
          ) : (
            years.map(year => (
              <div key={year} className="mb-6">
                <h2 className="text-white font-semibold text-lg mb-3">{year}</h2>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Month</TableHead>
                          <TableHead className="text-slate-400 text-center">Paid Days</TableHead>
                          <TableHead className="text-slate-400 text-right">Gross Earnings</TableHead>
                          <TableHead className="text-slate-400 text-right">Incentive</TableHead>
                          <TableHead className="text-slate-400 text-right">Deductions</TableHead>
                          <TableHead className="text-slate-400 text-right">Net Pay</TableHead>
                          <TableHead className="text-slate-400 text-center w-24">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groupedByYear[year].map(p => (
                          <TableRow key={p.payslip_id} className="border-slate-700 hover:bg-slate-700/50 cursor-pointer"
                            onClick={() => viewPayslip(p.payslip_id)} data-testid={`payslip-row-${p.month}`}>
                            <TableCell className="text-white font-medium">{p.month_name}</TableCell>
                            <TableCell className="text-slate-300 text-center">{p.paid_days}/{p.total_working_days}</TableCell>
                            <TableCell className={`text-right ${privacyOn ? 'text-slate-600 select-none' : 'text-slate-300'}`}>{amt(p.gross_earnings)}</TableCell>
                            <TableCell className={`text-right ${privacyOn ? 'text-slate-600 select-none' : 'text-amber-400'}`}>{privacyOn ? MASKED : (p.incentive > 0 ? formatCurrency(p.incentive) : '—')}</TableCell>
                            <TableCell className={`text-right ${privacyOn ? 'text-slate-600 select-none' : 'text-red-400'}`}>{amt(p.total_deductions)}</TableCell>
                            <TableCell className={`font-bold text-right ${privacyOn ? 'text-slate-600 select-none' : 'text-emerald-400'}`}>{amt(p.net_pay)}</TableCell>
                            <TableCell className="text-center">
                              <button className="text-slate-400 hover:text-emerald-400 transition-colors p-1"
                                data-testid={`view-payslip-${p.month}`}>
                                <Download className="w-4 h-4" />
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            ))
          )}
        </div>
        <Footer />
      </div>

      {/* Payslip Detail / Download Dialog */}
      <Dialog open={!!selectedPayslip} onOpenChange={() => setSelectedPayslip(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="sr-only">Payslip Preview</DialogTitle></DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : selectedPayslip ? (
            <PayslipPDF payslip={selectedPayslip} onClose={() => setSelectedPayslip(null)} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyPayslips;
